import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const profileLabels = {
  particulier: "Particulier",
  entreprise: "Entreprise",
  etudiant: "Étudiant",
  diaspora: "Diaspora",
};

const financingLabels = {
  "pret-personnel": "Prêt personnel",
  "avance-salaire": "Avance sur salaire",
  equipement: "Achat d’équipement",
  sante: "Santé",
  logement: "Logement",
  mariage: "Mariage",
  "projet-familial": "Projet familial",
  autre: "Autre besoin",
  tresorerie: "Avance de trésorerie",
  salaires: "Paiement des salaires",
  stock: "Achat de stock",
  materiel: "Achat de matériel",
  fournisseurs: "Paiement des fournisseurs",
  developpement: "Développement",
  investissement: "Investissement",
  "frais-universitaires": "Frais universitaires",
  ordinateur: "Achat d’un ordinateur",
  "logement-etudiant": "Logement étudiant",
  "voyage-etudes": "Voyage d’études",
  formation: "Formation",
  "investissement-guinee": "Investissement en Guinée",
  "aide-familiale": "Aide familiale",
  immobilier: "Immobilier",
  commerce: "Commerce",
  agriculture: "Agriculture",
  entrepreneuriat: "Entrepreneuriat",
};

const initialFormData = {
  firstName: "",
  lastName: "",
  birthDate: "",
  phone: "",
  email: "",
  city: "",

  companyName: "",
  registrationNumber: "",
  taxNumber: "",
  representativeName: "",

  requestedAmount: "",
  currency: "GNF",
  desiredDuration: "",
  durationUnit: "semaines",
  desiredDate: "",
  financingDescription: "",

  employmentStatus: "",
  employerOrActivity: "",
  monthlyIncome: "",
  monthlyExpenses: "",
  existingDebts: "non",
  currentDebtPayment: "",

  businessSector: "",
  creationYear: "",
  monthlyRevenue: "",
  businessMonthlyExpenses: "",
  employeeCount: "",
  repaymentSource: "",
  concernedEmployees: "",
  payrollAmount: "",
};

function FinanceApplicationForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState(initialFormData);

  const profile = searchParams.get("profil");
  const financingType = searchParams.get("type");

  const isCompany = profile === "entreprise";

  const profileLabel = profileLabels[profile] || "Non renseigné";
  const financingLabel =
    financingLabels[financingType] || "Non renseigné";

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((currentData) => ({
      ...currentData,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    navigate(
      `/consulting/finance/documents?profil=${profile}&type=${financingType}`,
      {
        state: {
          formData,
        },
      }
    );
  };

  return (
    <div style={styles.page}>
      <button
        type="button"
        style={styles.backButton}
        onClick={() =>
          navigate(
            `/consulting/finance/demande?profil=${profile || ""}&type=${
              financingType || ""
            }`
          )
        }
      >
        ← Retour au choix du financement
      </button>

      <main style={styles.content}>
        <div style={styles.introduction}>
          <div style={styles.step}>Étape 3 sur 5</div>

          <h1 style={styles.title}>
            Votre demande de financement
          </h1>

          <p style={styles.subtitle}>
            Complétez les informations nécessaires à l’étude de votre
            dossier.
          </p>
        </div>

        <div style={styles.summary}>
          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>
              Profil sélectionné
            </span>

            <strong style={styles.summaryValue}>
              {profileLabel}
            </strong>
          </div>

          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>
              Type de financement
            </span>

            <strong style={styles.summaryValue}>
              {financingLabel}
            </strong>
          </div>
        </div>

        <form style={styles.formCard} onSubmit={handleSubmit}>
          {/* SECTION 1 */}
          <div style={styles.formHeading}>
            <div style={styles.formNumber}>1</div>

            <div>
              <h2 style={styles.formTitle}>
                {isCompany
                  ? "Informations sur l’entreprise"
                  : "Informations personnelles"}
              </h2>

              <p style={styles.formDescription}>
                {isCompany
                  ? "Renseignez les informations principales de l’entreprise et de son représentant."
                  : "Renseignez vos informations d’identité et vos coordonnées."}
              </p>
            </div>
          </div>

          {isCompany ? (
            <div style={styles.fieldsGrid}>
              <FormField
                label="Nom de l’entreprise"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Exemple : HDC SARL"
                required
              />

              <FormField
                label="Nom du représentant"
                name="representativeName"
                value={formData.representativeName}
                onChange={handleChange}
                placeholder="Prénom et nom"
                required
              />

              <FormField
                label="Numéro RCCM"
                name="registrationNumber"
                value={formData.registrationNumber}
                onChange={handleChange}
                placeholder="Numéro d’immatriculation"
                required
              />

              <FormField
                label="Numéro d’identification fiscale"
                name="taxNumber"
                value={formData.taxNumber}
                onChange={handleChange}
                placeholder="Numéro NIF"
                required
              />

              <FormField
                label="Téléphone professionnel"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+224 000 00 00 00"
                required
              />

              <FormField
                label="Adresse e-mail"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="contact@entreprise.com"
                required
              />

              <FormField
                label="Ville"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Exemple : Conakry"
                required
              />
            </div>
          ) : (
            <div style={styles.fieldsGrid}>
              <FormField
                label="Prénom"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Votre prénom"
                autoComplete="given-name"
                required
              />

              <FormField
                label="Nom"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Votre nom"
                autoComplete="family-name"
                required
              />

              <FormField
                label="Date de naissance"
                name="birthDate"
                type="date"
                value={formData.birthDate}
                onChange={handleChange}
                autoComplete="bday"
                required
              />

              <FormField
                label="Téléphone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+224 000 00 00 00"
                autoComplete="tel"
                required
              />

              <FormField
                label="Adresse e-mail"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="exemple@email.com"
                autoComplete="email"
                required
              />

              <FormField
                label="Ville de résidence"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Exemple : Conakry"
                autoComplete="address-level2"
                required
              />
            </div>
          )}

          <div style={styles.formNotice}>
            <span>🔒</span>

            <p style={styles.formNoticeText}>
              Ces informations sont confidentielles et seront utilisées
              uniquement pour l’étude de votre demande.
            </p>
          </div>

          <div style={styles.sectionDivider} />

          {/* SECTION 2 */}
          <div style={styles.formHeading}>
            <div style={styles.formNumber}>2</div>

            <div>
              <h2 style={styles.formTitle}>
                Détails du financement
              </h2>

              <p style={styles.formDescription}>
                Précisez le montant, la durée souhaitée et l’utilisation
                prévue du financement.
              </p>
            </div>
          </div>

          <div style={styles.fieldsGrid}>
            <FormField
              label={`Montant demandé (${formData.currency})`}
              name="requestedAmount"
              type="number"
              value={formData.requestedAmount}
              onChange={handleChange}
              placeholder="Exemple : 5 000 000"
              min="1"
              required
            />

            <label style={styles.field}>
              <span style={styles.fieldLabel}>
                Devise du financement
                <span style={styles.required}> *</span>
              </span>

              <select
                style={styles.input}
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                required
              >
                <option value="GNF">
                  GNF — Franc guinéen
                </option>
                <option value="EUR">
                  EUR — Euro
                </option>
                <option value="USD">
                  USD — Dollar américain
                </option>
              </select>

              <span style={styles.fieldHelp}>
                Le franc guinéen (GNF) est sélectionné par défaut.
              </span>
            </label>

            <FormField
              label="Durée souhaitée"
              name="desiredDuration"
              type="number"
              value={formData.desiredDuration}
              onChange={handleChange}
              placeholder="Exemple : 2"
              min="1"
              required
            />

            <label style={styles.field}>
              <span style={styles.fieldLabel}>
                Unité de durée
                <span style={styles.required}> *</span>
              </span>

              <select
                style={styles.input}
                name="durationUnit"
                value={formData.durationUnit}
                onChange={handleChange}
                required
              >
                <option value="jours">Jours</option>
                <option value="semaines">Semaines</option>
                <option value="mois">Mois</option>
              </select>
            </label>

            <FormField
              label="Date souhaitée de mise à disposition"
              name="desiredDate"
              type="date"
              value={formData.desiredDate}
              onChange={handleChange}
              required
            />

            <label style={styles.fullWidthField}>
              <span style={styles.fieldLabel}>
                Description précise du besoin
                <span style={styles.required}> *</span>
              </span>

              <textarea
                style={styles.textarea}
                name="financingDescription"
                value={formData.financingDescription || ""}
                onChange={handleChange}
                placeholder="Expliquez votre besoin, l’utilisation prévue du montant et le résultat attendu."
                rows="5"
                maxLength="1200"
                required
              />

              <span style={styles.characterCount}>
                {(formData.financingDescription || "").length} / 1200
                caractères
              </span>
            </label>
          </div>

          <div style={styles.sectionDivider} />

          {/* SECTION 3 */}
          <div style={styles.formHeading}>
            <div style={styles.formNumber}>3</div>

            <div>
              <h2 style={styles.formTitle}>
                Capacité de remboursement
              </h2>

              <p style={styles.formDescription}>
                {isCompany
                  ? "Renseignez les principaux indicateurs financiers de l’entreprise."
                  : "Renseignez votre situation professionnelle, vos revenus et vos charges."}
              </p>
            </div>
          </div>

          {isCompany ? (
            <div style={styles.fieldsGrid}>
              <FormField
                label="Secteur d’activité"
                name="businessSector"
                value={formData.businessSector}
                onChange={handleChange}
                placeholder="Exemple : Commerce, transport, services"
                required
              />

              <FormField
                label="Année de création"
                name="creationYear"
                type="number"
                value={formData.creationYear}
                onChange={handleChange}
                placeholder="Exemple : 2020"
                min="1900"
                required
              />

              <FormField
                label="Chiffre d’affaires mensuel moyen (GNF)"
                name="monthlyRevenue"
                type="number"
                value={formData.monthlyRevenue}
                onChange={handleChange}
                placeholder="Exemple : 50 000 000"
                min="0"
                required
              />

              <FormField
                label="Charges mensuelles moyennes (GNF)"
                name="businessMonthlyExpenses"
                type="number"
                value={formData.businessMonthlyExpenses}
                onChange={handleChange}
                placeholder="Exemple : 20 000 000"
                min="0"
                required
              />

              <FormField
                label="Nombre total de salariés"
                name="employeeCount"
                type="number"
                value={formData.employeeCount}
                onChange={handleChange}
                placeholder="Exemple : 15"
                min="0"
                required
              />

              <FormField
                label="Source prévue du remboursement"
                name="repaymentSource"
                value={formData.repaymentSource}
                onChange={handleChange}
                placeholder="Exemple : encaissement client attendu"
                required
              />

              {financingType === "salaires" && (
                <>
                  <FormField
                    label="Nombre de salariés concernés"
                    name="concernedEmployees"
                    type="number"
                    value={formData.concernedEmployees}
                    onChange={handleChange}
                    placeholder="Exemple : 10"
                    min="1"
                    required
                  />

                  <FormField
                    label="Masse salariale à financer (GNF)"
                    name="payrollAmount"
                    type="number"
                    value={formData.payrollAmount}
                    onChange={handleChange}
                    placeholder="Exemple : 25 000 000"
                    min="1"
                    required
                  />
                </>
              )}
            </div>
          ) : (
            <div style={styles.fieldsGrid}>
              <label style={styles.field}>
                <span style={styles.fieldLabel}>
                  Situation professionnelle
                  <span style={styles.required}> *</span>
                </span>

                <select
                  style={styles.input}
                  name="employmentStatus"
                  value={formData.employmentStatus}
                  onChange={handleChange}
                  required
                >
                  <option value="">
                    Sélectionnez votre situation
                  </option>

                  <option value="salarie">Salarié(e)</option>

                  <option value="independant">
                    Indépendant(e)
                  </option>

                  <option value="entrepreneur">
                    Entrepreneur(e)
                  </option>

                  <option value="fonctionnaire">
                    Fonctionnaire
                  </option>

                  <option value="etudiant">Étudiant(e)</option>

                  <option value="sans-emploi">
                    Sans emploi
                  </option>

                  <option value="autre">Autre</option>
                </select>
              </label>

              <FormField
                label="Employeur ou activité exercée"
                name="employerOrActivity"
                value={formData.employerOrActivity}
                onChange={handleChange}
                placeholder="Nom de l’employeur ou description de l’activité"
                required
              />

              <FormField
                label="Revenu mensuel net (GNF)"
                name="monthlyIncome"
                type="number"
                value={formData.monthlyIncome}
                onChange={handleChange}
                placeholder="Exemple : 4 000 000"
                min="0"
                required
              />

              <FormField
                label="Charges mensuelles (GNF)"
                name="monthlyExpenses"
                type="number"
                value={formData.monthlyExpenses}
                onChange={handleChange}
                placeholder="Exemple : 1 500 000"
                min="0"
                required
              />

              <label style={styles.field}>
                <span style={styles.fieldLabel}>
                  Avez-vous des crédits ou dettes en cours ?
                  <span style={styles.required}> *</span>
                </span>

                <select
                  style={styles.input}
                  name="existingDebts"
                  value={formData.existingDebts}
                  onChange={handleChange}
                  required
                >
                  <option value="non">Non</option>
                  <option value="oui">Oui</option>
                </select>
              </label>

              {formData.existingDebts === "oui" && (
                <FormField
                  label="Remboursements mensuels en cours (GNF)"
                  name="currentDebtPayment"
                  type="number"
                  value={formData.currentDebtPayment}
                  onChange={handleChange}
                  placeholder="Exemple : 500 000"
                  min="0"
                  required
                />
              )}
            </div>
          )}

          <div style={styles.formActions}>
            <button
              type="button"
              style={styles.previousButton}
              onClick={() =>
                navigate(
                  `/consulting/finance/demande?profil=${profile}&type=${financingType}`
                )
              }
            >
              ← Modifier le financement
            </button>

            <button type="submit" style={styles.continueButton}>
              Continuer vers les justificatifs →
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

function FormField({
  label,
  name,
  type = "text",
  value,
  onChange,
  placeholder,
  autoComplete,
  min,
  required = false,
}) {
  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>
        {label}
        {required && <span style={styles.required}> *</span>}
      </span>

      <input
        style={styles.input}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        min={min}
        required={required}
      />
    </label>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    padding: "40px 24px 90px",
    background:
      "linear-gradient(180deg, #070b18 0px, #0b1020 510px, #f8fafc 510px, #f8fafc 100%)",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  backButton: {
    border: "none",
    padding: 0,
    background: "transparent",
    color: "#cbd5e1",
    fontSize: "13px",
    fontWeight: "800",
    cursor: "pointer",
  },

  content: {
    width: "100%",
    maxWidth: "1000px",
    margin: "70px auto 0",
  },

  introduction: {
    textAlign: "center",
    color: "#ffffff",
  },

  step: {
    color: "#a78bfa",
    fontSize: "11px",
    fontWeight: "900",
    letterSpacing: "0.16em",
    textTransform: "uppercase",
  },

  title: {
    margin: "16px 0 0",
    color: "#ffffff",
    fontSize: "clamp(34px, 5vw, 52px)",
    lineHeight: 1.1,
    letterSpacing: "-0.04em",
    fontWeight: "900",
  },

  subtitle: {
    margin: "18px auto 0",
    maxWidth: "620px",
    color: "#cbd5e1",
    fontSize: "15px",
    lineHeight: 1.8,
  },

  summary: {
    marginTop: "40px",
    padding: "20px",
    border: "1px solid rgba(255, 255, 255, 0.12)",
    borderRadius: "20px",
    background: "rgba(255, 255, 255, 0.07)",
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
  },

  summaryItem: {
    padding: "18px",
    borderRadius: "14px",
    background: "rgba(2, 6, 23, 0.65)",
  },

  summaryLabel: {
    display: "block",
    color: "#94a3b8",
    fontSize: "11px",
    fontWeight: "700",
  },

  summaryValue: {
    display: "block",
    marginTop: "8px",
    color: "#ffffff",
    fontSize: "15px",
  },

  formCard: {
    marginTop: "28px",
    padding: "32px",
    border: "1px solid #e2e8f0",
    borderRadius: "24px",
    background: "#ffffff",
    boxShadow: "0 20px 55px rgba(15, 23, 42, 0.1)",
  },

  formHeading: {
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
  },

  formNumber: {
    width: "42px",
    minWidth: "42px",
    height: "42px",
    borderRadius: "13px",
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "15px",
    fontWeight: "900",
  },

  formTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "22px",
    fontWeight: "900",
  },

  formDescription: {
    margin: "8px 0 0",
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.6,
  },

  fieldsGrid: {
    marginTop: "30px",
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "20px",
  },

  field: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  fieldLabel: {
    color: "#334155",
    fontSize: "12px",
    fontWeight: "800",
  },

  fieldHelp: {
    color: "#64748b",
    fontSize: "10px",
    lineHeight: 1.5,
  },

  required: {
    color: "#dc2626",
  },

  input: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "48px",
    padding: "0 14px",
    border: "1px solid #cbd5e1",
    borderRadius: "12px",
    background: "#ffffff",
    color: "#0f172a",
    fontFamily: "inherit",
    fontSize: "14px",
    outlineColor: "#7c3aed",
  },

  formNotice: {
    marginTop: "28px",
    padding: "14px 16px",
    borderRadius: "13px",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  formNoticeText: {
    margin: 0,
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.6,
  },

  sectionDivider: {
    height: "1px",
    margin: "34px 0",
    background: "#e2e8f0",
  },

  fullWidthField: {
    gridColumn: "1 / -1",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  textarea: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "130px",
    padding: "14px",
    border: "1px solid #cbd5e1",
    borderRadius: "12px",
    background: "#ffffff",
    color: "#0f172a",
    fontFamily: "inherit",
    fontSize: "14px",
    lineHeight: 1.6,
    resize: "vertical",
    outlineColor: "#7c3aed",
  },

  characterCount: {
    alignSelf: "flex-end",
    color: "#94a3b8",
    fontSize: "11px",
  },

  formActions: {
    marginTop: "38px",
    paddingTop: "26px",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
  },

  previousButton: {
    padding: "14px 20px",
    border: "1px solid #cbd5e1",
    borderRadius: "13px",
    background: "#ffffff",
    color: "#334155",
    fontSize: "13px",
    fontWeight: "800",
    cursor: "pointer",
  },

  continueButton: {
    padding: "15px 22px",
    border: "none",
    borderRadius: "13px",
    background: "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "900",
    cursor: "pointer",
    boxShadow: "0 12px 26px rgba(37, 99, 235, 0.2)",
  },
};

export default FinanceApplicationForm;